import { computed, onMounted, ref } from 'vue'
import dayjs from 'dayjs'
import AppMessage from '@/components/message/index.js'
import { CHANNEL_TAB_OPTIONS } from '@/pages/ai-board/components/channels/channels.js'
import { getSubscribeDetail, updateSubscribe } from '@/api/new-agent/subscribe.js'
import {
  SUBSCRIBE_TIME_TYPE_OPTIONS,
  SUBSCRIBE_WEEK_OPTIONS,
  SUBSCRIBE_MONTH_OPTIONS,
  parseDetailToForm,
  sortAsc
} from './subscribe.js'

// PC 与移动端共享详情回填、校验及提交协议。每次打开时挂载新的编辑器实例。
export const useSubscribeEditor = (props, { onClose, onSuccess }) => {
  const isDetailLoading = ref(true) // 是否正在拉取订阅详情
  const detailLoaded = ref(false)
  const detailFailed = ref(false)
  const timeTypeMenuVisible = ref(false)
  const dateMenuVisible = ref(false)
  const timeTypeOptions = SUBSCRIBE_TIME_TYPE_OPTIONS
  const weekOptions = SUBSCRIBE_WEEK_OPTIONS
  const monthOptions = SUBSCRIBE_MONTH_OPTIONS
  const subscribeForm = ref({
    name: '',
    description: '',
    timeType: 1,
    // 每周支持多选，元素取值 1-7（周一至周日）
    week: [],
    // 每月支持多选，元素取值 1-31
    monthDay: [],
    // 单次任务的执行日期（YYYY-MM-DD）
    date: '',
    time: '09:00',
    // 消息推送
    channelEnabled: true,
    targetType: 1, // 目标渠道类型：1 单聊，2 群聊
    // 单聊/群聊各自维护一个渠道 id，提交时按 targetType 合并为 channelId
    privateChannelId: '',
    groupChannelId: ''
  })

  // 已选星期的回显文案，按 weekOptions 顺序拼接；未选择时为空串，由模板兜底显示占位符
  const weekLabelText = computed(() =>
    weekOptions
      .filter((item) => subscribeForm.value.week.includes(item.key))
      .map((item) => item.label)
      .join('、')
  )

  // 已选日期的回显文案，按升序拼接；未选择时为空串，由模板兜底显示占位符
  const monthDayLabelText = computed(() =>
    subscribeForm.value.monthDay.length
      ? `${sortAsc(subscribeForm.value.monthDay).join('、')} 号`
      : ''
  )

  const isSubmitting = ref(false)
  // 单聊/群聊 tab，value 即 targetType（1 单聊 / 2 群聊），同时决定下方渲染哪个渠道选择器
  const targetTypeOptions = CHANNEL_TAB_OPTIONS

  const selectTimeType = (timeType) => {
    subscribeForm.value.timeType = timeType
    timeTypeMenuVisible.value = false
    dateMenuVisible.value = false
  }

  // 多选：已选中则移除，未选中则追加；面板保持展开以便连续选择
  const selectWeek = (week) => {
    const weeks = subscribeForm.value.week
    const index = weeks.indexOf(week)
    if (index === -1) {
      weeks.push(week)
    } else {
      weeks.splice(index, 1)
    }
  }

  const selectMonthDay = (monthDay) => {
    const monthDays = subscribeForm.value.monthDay
    const index = monthDays.indexOf(monthDay)
    if (index === -1) {
      monthDays.push(monthDay)
    } else {
      monthDays.splice(index, 1)
    }
  }

  // 单次任务只能选今天及以后：今天 0 点之前的日期置灰，当天本身可选
  const disabledPastDate = (date) => date.getTime() < new Date().setHours(0, 0, 0, 0)

  // 单次任务选今天时，时刻不能早于当前时刻；选明天及以后不限制
  const timePickerMinTime = computed(() => {
    const { timeType, date } = subscribeForm.value
    const now = dayjs()
    if (timeType !== 4 || date !== now.format('YYYY-MM-DD')) return ''
    return now.format('HH:mm')
  })

  // 切换单聊/群聊时不清空渠道：两种类型各自记住已选渠道
  const selectTargetType = (targetType) => {
    subscribeForm.value.targetType = targetType
  }

  // 打开弹窗后按 scheduleId 拉取详情回填表单
  const getEditDetail = async () => {
    if (!props.scheduleId) return

    isDetailLoading.value = true
    detailFailed.value = false
    try {
      const res = await getSubscribeDetail({ scheduleId: props.scheduleId })
      if (!res?.result) throw new Error('定时任务详情为空')
      Object.assign(subscribeForm.value, res.result, parseDetailToForm(res.result))
      detailLoaded.value = true
    } catch (error) {
      detailFailed.value = true
      console.log(error)
    } finally {
      isDetailLoading.value = false
    }
  }

  onMounted(getEditDetail)

  const closeDialog = () => {
    if (!isSubmitting.value) onClose()
  }

  // 执行时间的必填字段随类型变化：类型是几就校验它对应的字段，返回空串表示通过
  const validateSchedule = () => {
    const { timeType, time, week, monthDay, date } = subscribeForm.value
    if (!time) return '请选择执行时间'
    if (timeType === 2 && !week.length) return '请选择星期'
    if (timeType === 3 && !monthDay.length) return '请选择日期'
    if (timeType === 4 && !date) return '请选择执行日期'
    // 回填的历史单次任务，执行日期可能已经过去（面板里置灰了，但旧值仍在表单里），此时必须重新选日期
    if (timeType === 4 && dayjs(date).isBefore(dayjs().startOf('day')))
      return '执行日期不能早于今天'
    // 回填的历史任务、或切到「今天」后原来的时刻，都可能已经过去
    const minTime =
      timeType === 4 && date === dayjs().format('YYYY-MM-DD') ? dayjs().format('HH:mm') : ''
    if (minTime && time < minTime) return '执行时间不能早于当前时间'
    return ''
  }

  // 提交前按 timeType 组装执行时间字段：表单里可能残留其他类型选过的值（例如从「单次」切到「每天」后
  // 仍留着 date），这里只提交当前类型用得到的字段，避免脏字段一并提交给后端
  const buildSubmitPayload = () => {
    const {
      timeType,
      time,
      week,
      monthDay,
      date,
      targetType,
      privateChannelId,
      groupChannelId,
      ...rest
    } = subscribeForm.value
    // 1 每天只用 time，2 每周 + week，3 每月 + monthDay，4 单次 + date；多选按升序提交，存下来的顺序即展示顺序
    const scheduleFields =
      {
        2: { week: sortAsc(week) },
        3: { monthDay: sortAsc(monthDay) },
        4: { date }
      }[timeType] || {}

    // 后端只认一个 channelId，按 targetType 取对应类型已选的渠道
    const channelId = targetType === 1 ? privateChannelId : groupChannelId
    return { ...rest, timeType, time, targetType, channelId, ...scheduleFields }
  }

  const handleConfirm = async () => {
    if (isDetailLoading.value || isSubmitting.value || !detailLoaded.value) return
    if (!subscribeForm.value.name.trim()) {
      AppMessage.warning('名称不能为空')
      return
    }
    if (!subscribeForm.value.description.trim()) {
      AppMessage.warning('需求说明不能为空')
      return
    }
    const scheduleWarning = validateSchedule()
    if (scheduleWarning) {
      AppMessage.warning(scheduleWarning)
      return
    }
    // 推送开关打开时必须选好当前类型对应的渠道，否则会存下一个推送不到任何渠道的订阅
    const { targetType, channelEnabled, privateChannelId, groupChannelId } = subscribeForm.value
    if (channelEnabled && !(targetType === 1 ? privateChannelId : groupChannelId)) {
      AppMessage.warning('请选择推送渠道')
      return
    }
    isSubmitting.value = true
    try {
      await updateSubscribe(buildSubmitPayload())
      AppMessage.success('编辑成功')
      onClose()
      onSuccess()
    } catch (error) {
      console.log(error)
    } finally {
      isSubmitting.value = false
    }
  }

  const canSubmit = computed(() => {
    const form = subscribeForm.value
    return (
      detailLoaded.value &&
      !isDetailLoading.value &&
      !isSubmitting.value &&
      !!form.name.trim() &&
      !!form.description.trim() &&
      !validateSchedule() &&
      (!form.channelEnabled ||
        !!(form.targetType === 1 ? form.privateChannelId : form.groupChannelId))
    )
  })

  return {
    subscribeForm,
    isDetailLoading,
    detailFailed,
    isSubmitting,
    canSubmit,
    getEditDetail,
    timeTypeMenuVisible,
    dateMenuVisible,
    timeTypeOptions,
    weekOptions,
    monthOptions,
    targetTypeOptions,
    weekLabelText,
    monthDayLabelText,
    selectTimeType,
    selectWeek,
    selectMonthDay,
    disabledPastDate,
    timePickerMinTime,
    selectTargetType,
    closeDialog,
    handleConfirm
  }
}
